import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Property } from '@/types/property';

interface PriceVariationChartProps {
  properties: Property[];
  isMobile?: boolean;
  dashboardType?: 'venda' | 'aluguel' | 'sairam-ultima-semana' | 'entraram-ultima-semana'
}

interface VariationData {
  name: string;
  value: number;
}

const COLORS = ['#1e90ff', '#e6e9f0', '#ffa500']; // Blue for increase, grey for unchanged, orange for decrease

const PriceVariationChart: React.FC<PriceVariationChartProps> = ({ 
  properties,
  isMobile = false,
  dashboardType = 'venda'
}) => {
  const chartData: VariationData[] = useMemo(() => {
    if (!properties || properties.length === 0) return [];

    // Count properties by variation category - specifically handling null values as "Igual"
    const increased = properties.filter(p => p.variacao === 'Subiu').length;
    const decreased = properties.filter(p => p.variacao === 'Desceu').length;
    const unchanged = properties.filter(p => !p.variacao || p.variacao === 'Igual' || p.variacao === 'Manteve').length;

    return [
      { name: 'Subiu', value: increased },
      { name: 'Igual', value: unchanged },
      { name: 'Desceu', value: decreased },
    ].filter(item => item.value > 0);
  }, [properties]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0];
      const percentage = ((item.value / properties.length) * 100).toFixed(1);
      return (
        <div className="bg-white p-2 border shadow-md rounded-md text-xs md:text-sm">
          <p className="font-medium">{item.name}</p>
          <p>{`${item.value} imóveis ${dashboardType === 'venda' ? 'com preço' : 'com aluguel'} ${item.name.toLowerCase()} (${percentage}%)`}</p>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-xs md:text-sm text-muted-foreground">Dados insuficientes para exibir o gráfico</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={isMobile ? 40 : 60}
          outerRadius={isMobile ? 60 : 80}
          paddingAngle={5}
          dataKey="value"
          label={isMobile ? false : ({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
        >
          {chartData.map((entry, index) => {
            let color;
            if (entry.name === 'Subiu') color = COLORS[0];
            else if (entry.name === 'Igual') color = COLORS[1];
            else if (entry.name === 'Desceu') color = COLORS[2];
            else color = COLORS[index % COLORS.length];
            
            return <Cell key={`cell-${index}`} fill={color} />;
          })}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          formatter={(value) => (
            <span className="text-xs md:text-sm">
              {value}
            </span>
          )}
          wrapperStyle={isMobile ? { fontSize: '10px' } : { fontSize: '12px' }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default PriceVariationChart;
